import React from 'react';

import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import styled from 'styled-components';
import {v4} from 'uuid';

import {StaticImg} from 'app/components/general/StaticImg';
import {createFormErrorsContext} from 'app/general/forms';
import StateButton from 'app/general/widgets/StateButton';
import {WrapStaticContentPage} from 'app/general/WrapStaticContentPage';
import {LOGS_API_URL, WEBHOOKS_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';
import {del, get, patchJson, postJson} from 'app/utils/request';

import {DraftWebHook, EditableWebhook} from './EditableWebhook';
import {LogItem} from './LogItem';
import {WebhookItem} from './WebhookItem';

import {
  ListResponseType,
  WebHookEventType,
  WebHookLogSerializedType,
  WebHookSerializedType,
} from 'sweet-types';

import type {FormErrorsProps} from 'app/general/forms';

function BaseWebhookSettingsPage({
  setFormErrors,
  captureFormErrorsFromRejection,
}: FormErrorsProps) {
  const [webhooks, setWebhooks] = React.useState<
    Array<WebHookSerializedType | DraftWebHook>
  >([]);
  const [logs, setLogs] = React.useState<Array<WebHookLogSerializedType>>([]);
  const [draft, setDraft] = React.useState<null | DraftWebHook>(null);

  React.useEffect(() => {
    get(WEBHOOKS_API_URL).then(
      (response: ListResponseType<WebHookSerializedType>) =>
        setWebhooks(response.results)
    );
    get(LOGS_API_URL, {
      page_size: 5,
    }).then((response: ListResponseType<WebHookLogSerializedType>) =>
      setLogs(response.results)
    );
  }, []);

  const addBlank = () =>
    setDraft({
      id: v4(),
      target: '',
      is_active: true,
      event: '',
      created_at: '',
    });

  const save = (
    event: WebHookEventType,
    target: string,
    is_active: boolean
  ) => {
    setFormErrors({});

    return postJson(WEBHOOKS_API_URL, {
      event,
      target,
      is_active,
    }).then(
      (response: WebHookSerializedType) => {
        setWebhooks([...webhooks, response]);
        setDraft(null);
      },
      (error) => captureFormErrorsFromRejection(error)
    );
  };

  const remove = (webhook: WebHookSerializedType) =>
    del(`${WEBHOOKS_API_URL}${webhook.id}/`).then(() =>
      setWebhooks(webhooks.filter((hook) => hook.id !== webhook.id))
    );

  const update = (webhook: WebHookSerializedType) =>
    patchJson<WebHookSerializedType>(
      `${WEBHOOKS_API_URL}${webhook.id}/`,
      webhook
    ).then((updatedHook) =>
      setWebhooks(
        webhooks.map((hook) => (hook.id == updatedHook.id ? updatedHook : hook))
      )
    );

  return (
    <WrapStaticContentPage>
      {() => (
        <>
          <h1>{gt('Webhooks')}</h1>
          <p>
            {gt(
              'Need to connect SweetProcess to another system? We have you covered with webhooks. A webhook is a way for you to tell SweetProcess to contact another system when an important event happens. For example when a task is complete. Learn more in our guide:'
            )}{' '}
            <a href="https://www.sweetprocess.com/help/questions/130/webhooks-guide/">
              {gt('Webhook Guide')}
            </a>
          </p>

          <StyledHeaderRowDiv className="row py-2 d-none d-sm-flex">
            <div className="col-2 pr-0 d-flex align-items-center justify-content-center">
              {gt('Active')}
            </div>
            <div className="col-3 pr-0">{gt('Event')}</div>
            <div className="col-4 pr-0">{gt('Target URL')}</div>
            <div className="col-2 pr-0">{gt('Created')}</div>
            <div className="col-1 pr-0" />
          </StyledHeaderRowDiv>

          <div className="border-bottom d-sm-none" />

          {webhooks.length === 0 && draft == null ? (
            <div className="row border-bottom">
              <div className="col text-muted py-3 text-center">
                {gt('No webhooks created yet')}
              </div>
            </div>
          ) : (
            webhooks.map((webhook) =>
              webhook.id === draft?.id ? (
                <EditableWebhook
                  key={webhook.id}
                  webhook={webhook}
                  save={save}
                />
              ) : (
                <WebhookItem
                  remove={remove}
                  update={update}
                  webhook={webhook as WebHookSerializedType}
                  key={webhook.id}
                />
              )
            )
          )}

          <div
            className={cs('mb-4', {
              'text-right': draft == null,
            })}
          >
            {draft != null ? (
              <EditableWebhook key={draft.id} webhook={draft} save={save} />
            ) : (
              <StateButton
                className="py-2 btn btn-sm btn-link"
                onClick={addBlank}
                busyText={gt('Adding Webhook')}
              >
                <i className="fa fa-plus-circle mr-1" />
                {gt('Add New Webhook')}
              </StateButton>
            )}
          </div>

          <div className="card mt-5">
            <div className="card-header d-flex align-items-center">
              {gt('Latest Webhooks Usage')}
            </div>
            <div className="card-body py-0">
              {logs.length === 0 ? (
                <div className="row border-bottom">
                  <div className="col text-muted py-3 text-center">
                    {gt('No webhook was used yet.')}
                  </div>
                </div>
              ) : (
                logs.map((log) => <LogItem key={log.id} log={log} />)
              )}
            </div>
          </div>

          <div className="card mt-2">
            <div className="card-header d-flex align-items-center">
              <StaticImg
                alt={gt('Zapier Logo')}
                className="d-inline mr-2"
                style={{
                  maxHeight: '1.5rem',
                }}
                src="images/zapier-small-orange-logo.png"
              />{' '}
              {gt('Integration')}
            </div>
            <div className="card-body">
              <p className="card-text">
                {gt(
                  'We understand that connecting webhooks can be difficult, and not everyone is a developer. So we have integrated with Zapier which should make connecting to SweetProcess simpler. Learn more in our guide:'
                )}{' '}
                <a href="https://www.sweetprocess.com/kb/8LBTequD/article/ZkC1bcoeq/zapier-integration-guide/">
                  {gt('Zapier Integration Guide')}
                </a>
              </p>
              <div className="text-center">
                <a
                  className="btn btn-outline-secondary"
                  href="https://zapier.com/apps/sweetprocess/integrations"
                >
                  {gt('Add SweetProcess to Zapier')}
                </a>
              </div>
            </div>
          </div>

          <p className="mt-5">
            {gt(
              'If you need advice about setting up webhooks for your SweetProcess account, please contact us at %(email)s and our engineers will be happy to help.',
              {
                email: (
                  <a
                    className="text-nowrap"
                    href="mailto:support@sweetprocess.com"
                  >
                    <i className="far fa-envelope mr-1" />
                    support@sweetprocess.com
                  </a>
                ),
              }
            )}
          </p>
        </>
      )}
    </WrapStaticContentPage>
  );
}

export const WebhookSettingsPage = createFormErrorsContext(
  BaseWebhookSettingsPage
);

const StyledHeaderRowDiv = styled.div`
  text-transform: uppercase;
  font-weight: bold;
  font-size: 80%;

  border-bottom: 2px solid ${colors.borderGray};
`;
